import React, { useEffect, useMemo, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Space, Typography } from "antd";
import groupBy from "lodash.groupby";
import isEqual from "lodash.isequal";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { RequestStatus, teamActions, teamSelectors } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope, TeamMemberRole, TeamMemberStatus } from "@fitness-app/data-models";
import {
  normalizeTeamMemberChatAgentConfig,
  type TeamMemberWithUser,
} from "@fitness-app/data-models/entities/TeamMember";

import ModalForm from "~/components/ModalForm/ModalForm";
import { usePermission } from "~/hooks/usePermission";
import { TeamMemberTable } from "~/modules/Team/pages/TeamMemberList/TeamMemberTable";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { TeamMemberPermissionsModal } from "./TeamMemberPermissionsModal";
import TeamTrainerForm from "./TeamTrainerForm";
import { type TeamTrainerFormModel } from "./types";

const normalizeChatAgentConfigInput = (config?: TeamTrainerFormModel["chatAgentConfig"] | null) => {
  const normalized = normalizeTeamMemberChatAgentConfig(config);

  if (!normalized) {
    return null;
  }

  return {
    ...normalized,
    tone: normalized.tone || "",
    writingStyle: normalized.writingStyle || "",
    additionalInstructions: normalized.additionalInstructions || "",
  } as const;
};

const normalizeChatAgentConfig = (config?: TeamTrainerFormModel["chatAgentConfig"]) => {
  const normalized = normalizeTeamMemberChatAgentConfig(config);

  if (!normalized) {
    return null;
  }

  if (!normalized.enabled && !normalized.tone && !normalized.writingStyle && !normalized.additionalInstructions) {
    return null;
  }

  return {
    ...normalized,
    tone: normalized.tone?.trim() || null,
    writingStyle: normalized.writingStyle?.trim() || null,
    additionalInstructions: normalized.additionalInstructions?.trim() || null,
  };
};

const TeamMemberList = (): React.ReactElement => {
  const { t } = useTranslation(["team", "common"]);
  const dispatch = useAppDispatch();
  const trainers = useAppSelector(teamSelectors.getTrainersTeam) ?? [];
  const listStatus = useAppSelector((state) => state.team.listStatus);
  const [showLoader, toggleLoader] = useState(false);
  const [model, setModel] = useState<TeamTrainerFormModel | null>(null);
  const [permissionsMember, setPermissionsMember] = useState<TeamMemberWithUser | null>(null);
  const canManageMembership = usePermission(PermissionKey.TEAM_PERMISSIONS_MANAGE, PermissionScope.ALL);

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (listStatus === null) {
      dispatch(teamActions.subscribeToTeam());
    }
    void dispatch(teamActions.fetchTrainerConfig());
  }, [listStatus, dispatch]);

  const openForm = () => setIsOpen(true);
  const closeForm = () => {
    setIsOpen(false);
    setModel(null);
  };

  const addTrainer = async (formModel: TeamTrainerFormModel) => {
    toggleLoader(true);

    const { assignedTrainers, showOnlyNotAssigned, ...trainer } = formModel;
    const chatAgentConfig = normalizeChatAgentConfig(trainer.chatAgentConfig);

    if (model) {
      await dispatch(
        teamActions.updateTrainerInTeam({
          memberId: model.id,
          data: {
            role: trainer.role || model.role,
            specializationTags: trainer.specializationTags || [],
            status: trainer.status ? TeamMemberStatus.ACTIVE : TeamMemberStatus.INACTIVE,
            calendlyEmail: trainer.calendlyEmail || model.calendlyEmail || null,
            slackId: trainer.slackId || model.slackId || null,
            chatAgentConfig,
          },
          assignedTrainers: isEqual(model.assignedTrainers, assignedTrainers) ? null : assignedTrainers,
        }),
      );
    } else {
      const trainerData = {
        ...trainer,
        status: trainer.status ? TeamMemberStatus.PENDING : TeamMemberStatus.INACTIVE,
        id: uuid(),
        assignedTrainers,
        chatAgentConfig,
      };
      await dispatch(teamActions.addTrainerToTeam(trainerData));
    }
    toggleLoader(false);
    setModel(null);
    closeForm();
  };

  const openEditForm = (selected: TeamMemberWithUser) => {
    setModel({
      ...selected,
      specializationTags: selected.specializationTags || [],
      status: selected.status === TeamMemberStatus.ACTIVE,
      assignedTrainers: selected.assignedTrainers?.map((trainer) => trainer.trainer.id) || [],
      showOnlyNotAssigned: true,
      chatAgentConfig: normalizeChatAgentConfigInput(selected.chatAgentConfig) || {
        enabled: false,
        mode: "automatic",
        replyMode: "suggestion",
        sensitivity: "balanced",
        tone: "",
        writingStyle: "",
        additionalInstructions: "",
      },
      calendlyEmail: selected.calendlyEmail || "",
      slackId: selected.slackId || null,
      userId: selected.userId,
    });
    setIsOpen(true);
  };

  const groupByRole = useMemo(() => groupBy(trainers, "role"), [trainers]);

  const activeTrainers = useMemo(
    () =>
      trainers.filter(
        (trainer) => trainer.status === TeamMemberStatus.ACTIVE && trainer.role === TeamMemberRole.REGULAR_TRAINER,
      ).length,
    [trainers],
  );

  return (
    <Row>
      <Col xs={24}>
        <Card
          title={t("team")}
          loading={listStatus === RequestStatus.SUBSCRIBING}
          extra={
            <Space>
              {canManageMembership ? (
                <Button type="primary" icon={<PlusOutlined />} onClick={openForm}>
                  {t("common.addTrainer")}
                </Button>
              ) : null}
            </Space>
          }
        >
          <div className="w-full space-y-12">
            <div className="space-y-3">
              <Typography.Title level={4}>{t("role.account_owner")}</Typography.Title>
              <TeamMemberTable
                trainers={groupByRole[TeamMemberRole.ACCOUNT_OWNER] || []}
                role={TeamMemberRole.ACCOUNT_OWNER}
                openEditForm={openEditForm}
                openPermissions={setPermissionsMember}
              />
            </div>

            <div className="space-y-3">
              <Typography.Title level={4}>{t("role.administration")}</Typography.Title>
              <TeamMemberTable
                trainers={groupByRole[TeamMemberRole.ADMINISTRATION] || []}
                openEditForm={openEditForm}
                openPermissions={setPermissionsMember}
                role={TeamMemberRole.ADMINISTRATION}
              />
            </div>

            <div className="space-y-3">
              <Typography.Title level={4}>{t("role.head_coach")}</Typography.Title>
              <TeamMemberTable
                trainers={groupByRole[TeamMemberRole.SHARED_TRAINER] || []}
                openEditForm={openEditForm}
                openPermissions={setPermissionsMember}
                role={TeamMemberRole.SHARED_TRAINER}
              />
            </div>

            <div className="space-y-3">
              <Typography.Title level={4}>
                {t("role.trainers")} ({activeTrainers} {t("common.activated")})
              </Typography.Title>
              <TeamMemberTable
                trainers={groupByRole[TeamMemberRole.REGULAR_TRAINER] || []}
                openEditForm={openEditForm}
                openPermissions={setPermissionsMember}
              />
            </div>

            <div className="space-y-3">
              <Typography.Title level={4}>{t("role.sales_rep")}</Typography.Title>
              <TeamMemberTable
                trainers={groupByRole[TeamMemberRole.SALES_REP] || []}
                openEditForm={openEditForm}
                openPermissions={setPermissionsMember}
                role={TeamMemberRole.SALES_REP}
              />
            </div>

            <div className="space-y-3">
              <Typography.Title level={4}>{t("role.support")}</Typography.Title>
              <TeamMemberTable
                trainers={groupByRole[TeamMemberRole.SUPPORT] || []}
                openEditForm={openEditForm}
                openPermissions={setPermissionsMember}
                role={TeamMemberRole.SUPPORT}
              />
            </div>
          </div>
        </Card>
      </Col>
      <ModalForm
        title={model ? t("editTrainer") : t("common.addTrainer")}
        open={isOpen}
        onCancel={closeForm}
        loading={showLoader}
      >
        <TeamTrainerForm onSubmit={addTrainer} model={model || undefined} />
      </ModalForm>
      <TeamMemberPermissionsModal member={permissionsMember} onClose={() => setPermissionsMember(null)} />
    </Row>
  );
};

export default TeamMemberList;
